import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '@/components/feature/SEO';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/base/ArticleCard';
import { articles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import { siteInfo } from '@/mocks/site';

export default function Article() {
  const { slug } = useParams<{ slug: string }>();
  const { t } = useTranslation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as const });
  }, [slug]);

  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <main className="pt-16 md:pt-18 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-error-warning-line text-2xl"></i>
          </div>
          <h1 className="text-xl font-heading font-bold text-foreground-950 mb-2">文章未找到</h1>
          <p className="text-sm text-foreground-600 mb-6">抱歉，您要查看的文章不存在或已被移除。</p>
          <Link
            to="/"
            className="inline-flex px-6 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer"
          >
            {t('back_to_home')}
          </Link>
        </div>
      </main>
    );
  }

  const categoryObj = categories.find((c) => c.slug === article.category);
  const categoryName = categoryObj?.name || article.category;

  const relatedArticles = articles
    .filter((a) => a.category === article.category && a.id !== article.id)
    .slice(0, 3);

  const breadcrumbItems = [
    { label: t('breadcrumb_home'), href: '/' },
    { label: t('breadcrumb_categories'), href: '/category/ethical-theory' },
    { label: categoryName, href: `/category/${article.category}` },
    { label: article.title },
  ];

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbItems.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.label,
      item: item.href ? `${siteInfo.url}${item.href}` : undefined,
    })),
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.summary,
    author: {
      '@type': 'Person',
      name: article.author,
      jobTitle: article.authorTitle,
    },
    datePublished: article.date,
    publisher: {
      '@type': 'Organization',
      name: siteInfo.name,
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${siteInfo.url}/article/${article.slug}`,
    },
  };

  return (
    <>
      <SEO
        title={article.title}
        description={article.summary}
        keywords={`${article.title},${categoryName},伦理在线`}
        canonicalUrl={`${siteInfo.url}/article/${article.slug}`}
        ogType="article"
        schema={{ ...breadcrumbSchema, ...articleSchema }}
      />

      <main className="pt-16 md:pt-18">
        <div className="w-full px-4 md:px-6 bg-background-50 border-b border-background-200/70">
          <div className="max-w-3xl mx-auto">
            <Breadcrumb items={breadcrumbItems} />
          </div>
        </div>

        <article className="w-full px-4 md:px-6 py-8 md:py-12 bg-background-50">
          <div className="max-w-3xl mx-auto">
            <div className="relative w-full h-60 md:h-80 rounded-lg overflow-hidden mb-8">
              <img
                src={article.coverImage}
                alt={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <span className="inline-block px-2.5 py-1 rounded-md text-xs font-medium bg-secondary-100 text-secondary-800 mb-4">
              {categoryName}
            </span>

            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4 leading-tight">
              {article.title}
            </h1>

            <div className="flex flex-wrap items-center gap-4 md:gap-6 text-sm text-foreground-500 mb-8 pb-8 border-b border-background-200/70">
              <span className="flex items-center gap-1.5">
                <i className="ri-user-line"></i>
                {t('author_prefix')}{article.author}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                {t('publish_date')}{article.date}
              </span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                {t('read_time')}{article.readTime}{t('minute')}
              </span>
            </div>

            <div
              className="prose prose-base md:prose-lg max-w-none
                prose-headings:font-heading prose-headings:text-foreground-950 prose-headings:font-semibold
                prose-h2:text-xl md:prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
                prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:my-4
                prose-strong:text-foreground-900
                text-foreground-700"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-10 pt-8 border-t border-background-200/70">
              <Link
                to={`/category/${article.category}`}
                className="inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 transition-colors cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                {t('back_to_list')}
              </Link>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
            <div className="max-w-6xl mx-auto">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-6 md:mb-8">
                {t('related_articles')}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {relatedArticles.map((ra) => (
                  <ArticleCard
                    key={ra.id}
                    slug={ra.slug}
                    title={ra.title}
                    summary={ra.summary}
                    category={ra.category}
                    author={ra.author}
                    date={ra.date}
                    readTime={ra.readTime}
                    coverImage={ra.coverImage}
                  />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
    </>
  );
}